import classNames from "classnames";
import React, { useRef, useState } from "react";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
} from "react-hook-form";
import { LuPlus, LuSearch, LuTags, LuUsers } from "react-icons/lu";

import { SettingsInput } from "../../../../../components/inputs/SettingsInput";
import {
  Avatar,
  AvatarItem,
  AvatarStack,
} from "../../../../../components/newUI/avatar";
import { Button } from "../../../../../components/newUI/button";
import { Select, SelectOption } from "../../../../../components/newUI/select";
import { FieldLabel } from "../../../../../components/newUI/text";
import CustomSelect from "../../../../../components/select/Select";
import { SelectOptionType } from "../../../../../types/types";
import { ADD_WORK_INPUT_CLASSES } from "../model/addWork.constants";
import {
  AddWorkContactOption,
  AddWorkContactTagOption,
  AddWorkFormData,
} from "../model/addWork.types";
import { withCreatedContact } from "../utils/addWork.helpers";
import { CreateContactFromWork } from "./CreateContactFromWork";

type WorkDetailsSectionProps = {
  control: Control<AddWorkFormData>;
  errors: FieldErrors<AddWorkFormData>;
  register: UseFormRegister<AddWorkFormData>;
  workTypeOptions: SelectOptionType[];
  templateOptions: SelectOptionType[];
  contactsOptions: AddWorkContactOption[];
  contactTagOptions: AddWorkContactTagOption[];
  contactSelectTab: "contacts" | "tags";
  selectedContactTag: AddWorkContactTagOption | null;
  searchType: string;
  searchTemplate: string;
  searchContacts: string;
  setContactSelectTab: (value: "contacts" | "tags") => void;
  setSelectedContactTag: (value: AddWorkContactTagOption | null) => void;
  setSearchType: (value: string) => void;
  setSearchTemplate: (value: string) => void;
  setSearchContacts: (value: string) => void;
  handleLoadMoreContacts: () => void;
  hasMoreContacts: boolean;
  handleWorkTypeChange: (
    newWorkType: SelectOptionType | null,
    onChange: (value: SelectOptionType | null) => void,
  ) => void;
  isLoadingWorkTypes: boolean;
  isLoadingTemplates: boolean;
  isLoadingContactsFirstPage: boolean;
  isLoadingContactTags: boolean;
  isLoadingMoreContacts: boolean;
  isLoadingWorkTypeDefaults: boolean;
  hideContactSelect?: boolean;
};

const mapContactToAvatarItem = (contact: AddWorkContactOption): AvatarItem => ({
  id: contact.value,
  label: contact.avatar?.label,
  fullName: contact.label,
  color: contact.avatar?.color,
});

type SelectChangeValue = SelectOption | SelectOption[] | null;
type ContactTab = WorkDetailsSectionProps["contactSelectTab"];

const getSingleSelectOption = <TOption extends SelectOption>(
  value: SelectChangeValue,
): TOption | null => {
  if (Array.isArray(value)) {
    return (value[0] as TOption | undefined) ?? null;
  }

  return (value as TOption | null) ?? null;
};

const getMultiSelectOptions = <TOption extends SelectOption>(
  value: SelectChangeValue,
): TOption[] => (Array.isArray(value) ? (value as TOption[]) : []);

type SelectedContactsPreviewProps = {
  contacts: AddWorkContactOption[];
};

const SelectedContactsPreview: React.FC<SelectedContactsPreviewProps> = ({
  contacts,
}) => {
  if (!contacts.length) {
    return null;
  }

  const avatarItems = contacts.map(mapContactToAvatarItem);
  const contactCountLabel =
    contacts.length === 1
      ? "1 contact selected"
      : `${contacts.length} contacts selected`;

  return (
    <div
      className={
        "mt-3 flex items-center gap-6 rounded-[10px] border border-[#C4B5FD] bg-[#FBFAFF] px-[13px] py-[11px]"
      }>
      <AvatarStack
        items={avatarItems}
        size={"sm"}
        membersListTitle={"Selected contacts"}
        avatarClassNames={"ring-[#F7F5FF]"}
        avatarCountClassNames={"ring-[#E7E1FB] bg-white"}
      />
      <div className={"min-w-0 truncate"}>
        <p className={"text-sm font-bold leading-[1.5] text-[#3a4652]"}>
          {contactCountLabel}
        </p>
        <p className={"text-xs text-[#9097A8]"}>
          A separate work item will be created for each contact
        </p>
      </div>
    </div>
  );
};

export const WorkDetailsSection: React.FC<WorkDetailsSectionProps> = ({
  control,
  errors,
  register,
  workTypeOptions,
  templateOptions,
  contactsOptions,
  contactTagOptions,
  contactSelectTab,
  selectedContactTag,
  searchType,
  searchTemplate,
  searchContacts,
  setContactSelectTab,
  setSelectedContactTag,
  setSearchType,
  setSearchTemplate,
  setSearchContacts,
  handleLoadMoreContacts,
  hasMoreContacts,
  handleWorkTypeChange,
  isLoadingWorkTypes,
  isLoadingTemplates,
  isLoadingContactsFirstPage,
  isLoadingContactTags,
  isLoadingMoreContacts,
  isLoadingWorkTypeDefaults,
  hideContactSelect,
}) => {
  const isTagsTab = contactSelectTab === "tags";
  // "+ Create new contact" (NEWW-022/023): which window is open, and the Contact box's
  // latest value/onChange so the new client can be selected when it's created.
  const [createStep, setCreateStep] = useState<
    "choose" | "individual" | "organization" | null
  >(null);
  const contactField = useRef<{
    value: AddWorkContactOption[];
    onChange: (value: AddWorkContactOption[]) => void;
  } | null>(null);

  return (
    <>
      <CreateContactFromWork
        step={createStep}
        setStep={setCreateStep}
        onCreated={(created) => {
          const field = contactField.current;
          if (!field) return;
          setContactSelectTab("contacts");
          setSelectedContactTag(null);
          field.onChange(withCreatedContact(field.value || [], created));
        }}
      />
      <div>
        <SettingsInput
          {...register("workName", {
            validate: (value) =>
              !value.trim() ? "This field is required" : true,
            maxLength: {
              value: 250,
              message: "Work name shouldn't be more than 250 characters",
            },
          })}
          label={"Work Name"}
          required
          placeholder={"Type work name..."}
          variant={"form"}
          size={"md"}
          inputClasses={classNames("w-full", ADD_WORK_INPUT_CLASSES)}
          isError={!!errors?.workName}
        />
        {errors?.workName?.message && (
          <div className={"mt-2 text-sm font-bold text-error"}>
            {errors?.workName?.message}
          </div>
        )}
      </div>

      <div className={"grid grid-cols-2 gap-4"}>
        <div>
          <FieldLabel label={"Work Type"} required />
          <Controller
            name={"workType"}
            control={control}
            rules={{
              required: "This field is required",
            }}
            render={({ field: { value, onChange } }) => (
              <CustomSelect
                options={workTypeOptions || []}
                value={value}
                inputValue={searchType}
                onChangeInput={setSearchType}
                onChange={(newValue) => {
                  const newWorkType = newValue as SelectOptionType | null;
                  handleWorkTypeChange(newWorkType, onChange);
                }}
                withMenu
                isLoading={isLoadingWorkTypes}
                isError={!!errors?.workType}
                selectStyle={{ fullWidth: true, size: "md" }}
              />
            )}
          />
          {errors?.workType?.message && (
            <div className={"mt-2 text-sm font-bold text-error"}>
              {errors?.workType?.message}
            </div>
          )}
          {isLoadingWorkTypeDefaults && (
            <div className={"mt-2 text-xs font-medium text-[#6B7280]"}>
              Loading work type defaults...
            </div>
          )}
        </div>

        <div>
          <FieldLabel label={"Subtask Template"} />
          <Controller
            name={"subtaskTemplate"}
            control={control}
            render={({ field: { value, onChange } }) => (
              <CustomSelect
                options={templateOptions || []}
                value={value}
                inputValue={searchTemplate}
                onChangeInput={setSearchTemplate}
                withMenu
                onChange={(newValue) => {
                  onChange(newValue as SelectOptionType);
                }}
                isLoading={isLoadingTemplates || isLoadingWorkTypeDefaults}
                selectStyle={{ fullWidth: true, size: "md" }}
              />
            )}
          />
          {errors?.subtaskTemplate?.message && (
            <div className={"mt-2 text-sm font-bold text-error"}>
              {errors?.subtaskTemplate?.message}
            </div>
          )}
        </div>
      </div>

      <div>
        <FieldLabel label={"Contact"} required />
        <Controller
          name={"contact"}
          control={control}
          rules={{
            validate: (value) =>
              value.length > 0 ? true : "This field is required",
          }}
          render={({ field: { value, onChange } }) => {
            contactField.current = {
              value: isTagsTab ? [] : value,
              onChange,
            };
            return (
              <>
                {!hideContactSelect && (
                  <Select
                    options={isTagsTab ? contactTagOptions : contactsOptions}
                    value={isTagsTab ? selectedContactTag ?? null : value}
                    onChange={(newValue) => {
                      if (isTagsTab) {
                        const nextTag =
                          getSingleSelectOption<AddWorkContactTagOption>(
                            newValue,
                          );
                        const shouldClearTag =
                          nextTag?.value === selectedContactTag?.value;
                        const selectedTag = shouldClearTag ? null : nextTag;

                        setSelectedContactTag(selectedTag);
                        onChange(selectedTag ? selectedTag.contacts : []);
                        return;
                      }

                      setSelectedContactTag(null);
                      onChange(
                        getMultiSelectOptions<AddWorkContactOption>(newValue),
                      );
                    }}
                    isMulti={!isTagsTab}
                    renderValue={(selected) => {
                      // Contacts can run into the hundreds, so no chips:
                      // one name, or "N contacts selected" (owner's rule).
                      if (!Array.isArray(selected)) return undefined;
                      return (
                        <span
                          className={
                            "min-w-0 truncate text-sm font-medium text-[#1F2433]"
                          }>
                          {selected.length === 1
                            ? selected[0].label
                            : `${selected.length} contacts selected`}
                        </span>
                      );
                    }}
                    closeOnSelect={false}
                    tabs={[
                      {
                        label: "Contacts",
                        value: "contacts",
                      },
                      {
                        label: "Tags",
                        value: "tags",
                      },
                    ]}
                    activeTab={contactSelectTab}
                    onTabChange={(nextTab) => {
                      const tab = nextTab as ContactTab;

                      if (tab === contactSelectTab) {
                        return;
                      }

                      setContactSelectTab(tab);

                      if (tab === "contacts") {
                        if (selectedContactTag) {
                          onChange([]);
                        }

                        setSelectedContactTag(null);
                        return;
                      }

                      if (!selectedContactTag && value.length) {
                        onChange([]);
                      }
                    }}
                    search={{
                      enabled: true,
                      mode: isTagsTab ? "internal" : "external",
                      placement: "control",
                      value: isTagsTab ? undefined : searchContacts,
                      placeholder: isTagsTab
                        ? "Search tags..."
                        : "Search contacts...",
                      loading: isTagsTab
                        ? isLoadingContactTags
                        : isLoadingContactsFirstPage,
                      onChange: isTagsTab ? undefined : setSearchContacts,
                    }}
                    clearOnClose
                    loading={
                      isTagsTab
                        ? isLoadingContactTags
                        : isLoadingContactsFirstPage
                    }
                    loadingMore={!isTagsTab && isLoadingMoreContacts}
                    hasMore={!isTagsTab && hasMoreContacts}
                    onLoadMore={!isTagsTab ? handleLoadMoreContacts : undefined}
                    error={!!errors?.contact}
                    icon={<LuSearch size={16} />}
                    placeholder={"Select..."}
                    emptyLabel={
                      isTagsTab ? "No tags found" : "No contacts found"
                    }
                    emptyState={
                      isTagsTab
                        ? {
                            icon: <LuTags size={19} strokeWidth={1.9} />,
                            title: "No tags found",
                            description:
                              "Create tags in settings to group contacts.",
                          }
                        : {
                            icon: <LuUsers size={19} strokeWidth={1.9} />,
                            title: "No contacts found",
                            description:
                              "Try adjusting your search or check active contacts.",
                          }
                    }
                    styleConfig={{ fullWidth: true }}
                    dropMenuMaxHeight={280}
                    checkboxSize={"md"}
                    footer={{
                      render: ({ clear, done }) => (
                        <div
                          className={
                            "flex items-center justify-between gap-3 px-1 pb-0 pt-0.5"
                          }>
                          <span className={"flex items-center gap-3"}>
                            <Button
                              type={"button"}
                              variant={"ghost"}
                              size={"sm"}
                              autoWidth
                              className={"!h-auto p-0 text-xs font-bold"}
                              onClick={clear}>
                              Clear all
                            </Button>
                            {!isTagsTab && (
                              <Button
                                type={"button"}
                                variant={"ghost"}
                                size={"sm"}
                                autoWidth
                                leftIcon={
                                  <LuPlus size={13} strokeWidth={2.4} />
                                }
                                className={
                                  "!h-auto p-0 text-xs font-bold !text-[#6B4EFF]"
                                }
                                onClick={() => {
                                  done();
                                  setCreateStep("choose");
                                }}>
                                Create new contact
                              </Button>
                            )}
                          </span>
                          <Button
                            type={"button"}
                            size={"sm"}
                            autoWidth
                            className={
                              "!h-auto rounded-[7px] px-4 py-1.5 text-xs font-bold"
                            }
                            onClick={done}>
                            Done
                          </Button>
                        </div>
                      ),
                    }}
                    renderOption={(option) =>
                      isTagsTab ? (
                        <>
                          <div
                            className={
                              "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#F0EDFF] text-[#6B4EFF]"
                            }>
                            <LuTags size={16} />
                          </div>
                          <div className={"min-w-0 flex-1"}>
                            <p
                              className={
                                "block truncate text-sm font-semibold text-[#1F2433]"
                              }>
                              {option.label}
                            </p>
                            {option.caption && (
                              <p
                                className={
                                  "block truncate text-xs text-[#9097A8]"
                                }>
                                {option.caption}
                              </p>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          {option.avatar && <Avatar {...option.avatar} />}
                          <div className={"min-w-0 flex-1"}>
                            <p
                              className={
                                "block truncate text-sm font-semibold text-[#1F2433]"
                              }>
                              {option.label}
                            </p>
                          </div>
                        </>
                      )
                    }
                  />
                )}
                <SelectedContactsPreview contacts={value} />
              </>
            );
          }}
        />
        {errors?.contact?.message && (
          <div className={"mt-2 text-sm font-bold text-error"}>
            {errors?.contact?.message}
          </div>
        )}
      </div>
    </>
  );
};
